import {
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { Flame, Folder, HelpCircle, LogOut, Shield, User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserAvatar } from "../../components/UserAvatar";
import { SPACING } from "../../constants/spacing";
import { useAuth } from "../../contexts/AuthContext";

function UserDropdown() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Use user object if available, fallback to token decoded values
  const displayName = user?.name;
  const displayEmail = user?.email;
  const displayRole = user?.role;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Box sx={{ position: "relative" }}>
      <ClickAwayListener onClickAway={handleClose}>
        <Box>
          <IconButton
            onClick={handleToggle}
            size="small"
            sx={{ p: 0 }}
            data-testid="user-menu-button"
          >
            <UserAvatar
              name={displayName}
              image={user?.avatar}
              size={23}
              sx={{ cursor: "pointer" }}
            />
          </IconButton>

          {isOpen && (
            <Paper
              elevation={0}
              sx={{
                position: "absolute",
                top: 38,
                right: 0,
                width: 290,
                mt: 1,
                borderRadius: 2,
                zIndex: 1300,
                backgroundColor: "background.paper",
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* User Info Header */}
              <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
                <UserAvatar name={displayName} image={user?.avatar} size={30} />
                <Box sx={{ ml: 2, display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="bodySm"
                    sx={{
                      color: "text.primary",
                      fontWeight: 500,
                    }}
                  >
                    {displayName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {displayEmail}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {/* Menu Items */}
              <List sx={{ py: 1 }}>
                {displayRole === "admin" && (
                  <ListItem disablePadding>
                    <ListItemButton
                      component={Link}
                      to="/admin/dashboard"
                      onClick={handleClose}
                      sx={{
                        borderRadius: 1,
                        mx: SPACING.xs,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 40, color: "text.primary" }}
                      >
                        <Shield size={18} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("header.userMenu.adminPanel")}
                        primaryTypographyProps={{
                          variant: "bodySm",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}

                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/account"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 1,
                      mx: SPACING.xs,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: "text.primary" }}>
                      <User size={18} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("header.userMenu.myAccount")}
                      primaryTypographyProps={{
                        variant: "bodySm",
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/my-projects"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 1,
                      mx: SPACING.xs,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: "text.primary" }}>
                      <Folder size={18} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("header.userMenu.myProjects")}
                      primaryTypographyProps={{
                        variant: "bodySm",
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/support"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 1,
                      mx: SPACING.xs,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: "text.primary" }}>
                      <HelpCircle size={18} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("header.userMenu.contactSupport")}
                      primaryTypographyProps={{
                        variant: "bodySm",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/account/membership"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 1,
                      mx: SPACING.xs,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: "text.primary" }}>
                      <Flame size={18} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("header.userMenu.explorePremium")}
                      primaryTypographyProps={{
                        variant: "bodySm",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>

              <Divider />

              {/* Logout */}
              <List sx={{ py: 1 }}>
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/logout"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 1,
                      mx: SPACING.xs,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LogOut size={18} color="#dc3545" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("header.userMenu.logout")}
                      primaryTypographyProps={{
                        variant: "bodySm",
                        color: "#dc3545",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Paper>
          )}
        </Box>
      </ClickAwayListener>
    </Box>
  );
}

export default UserDropdown;
